import React, { useCallback, useEffect, useState } from 'react';
import { ShoppingCartIcon, TrendingUpIcon, PackageIcon, BarChart3Icon, FileTextIcon, RefreshCwIcon, ChevronDownIcon, ChevronUpIcon, UserIcon } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { StatsCard } from '../components/ui/StatsCard';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { Pagination } from '../components/ui/Pagination';
import { PageHeader } from '../components/ui/PageHeader';
import { supabaseAdmin } from '../lib/supabase';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
interface SaleItem {
  id: string;
  clienteNombre: string;
  vendedorId: string;
  vendedorNombre: string;
  fecha: Date;
  zona: string;
  montoTotal: number;
  subTotal: number;
  itbis: number;
  referencia: string;
  comentarios: string;
  source: 'visita' | 'factura';
}
export function SalesPage() {
  const {
    user
  } = useAuth();
  const {
    users,
    totalVendido: totalVendidoGlobal,
    isLoading: appLoading
  } = useApp();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const canSeeAll = isAdmin || user?.permisos?.cuentas_cobrar === true;
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [showVendorStats, setShowVendorStats] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 8;

  // Only actual vendedores
  const vendedorIds = new Set(users.filter(u => u.activo && u.permisos?.['new-visit'] === true).map(u => u.id));
  const vendedorNames = new Set(users.filter(u => u.activo && u.permisos?.['new-visit'] === true).map(u => u.nombre));

  // Load invoices from Supabase
  const [invoices, setInvoices] = useState<any[]>([]);
  const loadInvoices = useCallback(async () => {
    try {
      const {
        data,
        error
      } = await supabaseAdmin.from('invoices').select('id,numero_factura,farmacia,vendedor,vendedor_id,fecha_emision,sub_total,itbis,monto,estado').not('estado', 'eq', 'anulada').gte('fecha_emision', new Date(Date.now() - 90 * 864e5).toISOString().split('T')[0]).order('fecha_emision', {
        ascending: false
      }).limit(300);
      if (data) setInvoices(data);
      if (error) console.error('Error loading invoices for sales:', error);
    } catch {}
  }, []);
  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);
  useRealtimeRefresh(['invoices'], loadInvoices);
  const vendedorIdToName: Record<string, string> = {};
  users.filter(u => u.activo && u.permisos?.['new-visit'] === true).forEach(u => {
    vendedorIdToName[u.id] = u.nombre;
  });
  const vendedorNameNormalized: Record<string, string> = {};
  users.filter(u => u.activo && u.permisos?.['new-visit'] === true).forEach(u => {
    vendedorNameNormalized[u.nombre.trim().toLowerCase()] = u.nombre;
  });
  const allSales = invoices.map(inv => ({
    id: `f-${inv.id}`,
    clienteNombre: inv.farmacia,
    vendedorId: inv.vendedor_id || '',
    vendedorNombre: inv.vendedor || '',
    fecha: new Date(inv.fecha_emision),
    zona: '',
    montoTotal: inv.monto || 0,
    subTotal: inv.sub_total || 0,
    itbis: inv.itbis || 0,
    referencia: inv.numero_factura || '',
    comentarios: '',
    source: 'factura' as const
  }) as SaleItem).filter(s => canSeeAll || s.vendedorId === user?.id).filter(s => {
    const saleDate = s.fecha.toLocaleDateString('en-CA', {
      timeZone: 'America/Santo_Domingo'
    });
    if (dateFrom && saleDate < dateFrom) return false;
    if (dateTo && saleDate > dateTo) return false;
    return true;
  }).filter(s => {
    if (vendorFilter === 'all') return true;
    return s.vendedorId === vendorFilter || s.vendedorNombre === vendorFilter;
  }).sort((a, b) => b.fecha.getTime() - a.fecha.getTime());
  const totalVendido = allSales.reduce((sum, s) => sum + s.montoTotal, 0);
  const totalPedidos = allSales.length;
  const promedioVenta = totalPedidos > 0 ? totalVendido / totalPedidos : 0;
  const today = new Date();
  const todaySales = allSales.filter(s => s.fecha.toDateString() === today.toDateString());
  const todayTotal = todaySales.reduce((sum, s) => sum + s.montoTotal, 0);
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 0
  }).format(amount);
  const formatDate = (date: Date) => date.toLocaleDateString('es-DO', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  const totalPages = Math.ceil(allSales.length / ITEMS_PER_PAGE);
  const paginatedSales = allSales.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const salesByVendor = isAdmin ? allSales.filter(s => vendedorIds.has(s.vendedorId) || vendedorNames.has(s.vendedorNombre) || vendedorNameNormalized[s.vendedorNombre.trim().toLowerCase()]).reduce((acc, s) => {
    const name = vendedorIdToName[s.vendedorId] || vendedorNameNormalized[s.vendedorNombre.trim().toLowerCase()] || s.vendedorNombre || 'Sin vendedor';
    if (!acc[name]) acc[name] = {
      total: 0,
      count: 0
    };
    acc[name].total += s.montoTotal;
    acc[name].count += 1;
    return acc;
  }, {} as Record<string, {
    total: number;
    count: number;
  }>) : {};
  const activeVendors = users.filter(u => u.activo && u.rolNombre !== 'Administrador' && u.permisos?.['new-visit'] === true);
  const maxVendorTotal = Math.max(...Object.values(salesByVendor).map(d => d.total), 1);

  // Avatar initials color palette
  const avatarColors = ['bg-blue-100 text-blue-700', 'bg-emerald-100 text-emerald-700', 'bg-violet-100 text-violet-700', 'bg-amber-100 text-amber-700', 'bg-rose-100 text-rose-700', 'bg-cyan-100 text-cyan-700'];
  const getAvatarColor = (name: string) => avatarColors[name.charCodeAt(0) % avatarColors.length];
  return <div className="space-y-4 sm:space-y-6" data-id="element-4410">
      <PageHeader title="Registro de Ventas" subtitle="Historial de ventas y facturación" icon={<ShoppingCartIcon className="w-6 h-6" data-id="element-4412" />} data-id="element-4411">
        <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap" data-id="element-4413">
          <div className="flex items-center gap-1.5" data-id="element-4414">
            <span className="text-xs text-slate-500 font-medium" data-id="element-4415">Desde</span>
            <input type="date" value={dateFrom} onChange={e => {
            setDateFrom(e.target.value);
            setCurrentPage(1);
          }} className="rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-4416" />
          </div>
          <div className="flex items-center gap-1.5" data-id="element-4417">
            <span className="text-xs text-slate-500 font-medium" data-id="element-4418">Hasta</span>
            <input type="date" value={dateTo} onChange={e => {
            setDateTo(e.target.value);
            setCurrentPage(1);
          }} className="rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-4419" />
          </div>
          <Button variant="secondary" onClick={() => loadInvoices()} title="Recargar" data-id="element-4420">
            <RefreshCwIcon className="w-4 h-4" data-id="element-4421" />
          </Button>
          {(dateFrom || dateTo || vendorFilter !== 'all') && <Button variant="secondary" onClick={() => {
          setDateFrom('');
          setDateTo('');
          setVendorFilter('all');
        }} data-id="element-4422">
              Limpiar
            </Button>}
        </div>
      </PageHeader>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 animate-stagger-1" data-id="element-4423">
        <StatsCard title="Total Vendido" value={formatCurrency(totalVendido)} icon={<ShoppingCartIcon className="w-5 h-5" data-id="element-4425" />} variant="primary" data-id="element-4424" />
        <StatsCard title="Ventas Hoy" value={formatCurrency(todayTotal)} subtitle={`${todaySales.length} registros`} icon={<TrendingUpIcon className="w-5 h-5" data-id="element-4427" />} variant="success" data-id="element-4426" />
        <StatsCard title="Total Registros" value={totalPedidos} icon={<PackageIcon className="w-5 h-5" data-id="element-4429" />} variant="warning" data-id="element-4428" />
        <StatsCard title="Promedio por Venta" value={formatCurrency(promedioVenta)} icon={<BarChart3Icon className="w-5 h-5" data-id="element-4431" />} variant="default" data-id="element-4430" />
      </div>

      {/* Vendor filter + list — full width vertical */}
      <Card padding="none" className="animate-stagger-2" data-id="element-4432">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-id="element-4433">
          <div data-id="element-4434">
            <h2 className="text-base font-bold text-slate-900" data-id="element-4435">Historial de Ventas</h2>
            <p className="text-xs text-slate-500 mt-0.5" data-id="element-4436">{allSales.length} ventas registradas</p>
          </div>
          {/* Vendor filter (admin only) */}
          {isAdmin && activeVendors.length > 0 && <div className="flex items-center gap-2 flex-wrap" data-id="element-4437">
              <button onClick={() => {
            setVendorFilter('all');
            setCurrentPage(1);
          }} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${vendorFilter === 'all' ? 'bg-[#2952C4] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-4438">
                Todos
              </button>
              {activeVendors.map(v => <button key={v.id} onClick={() => {
            setVendorFilter(v.id);
            setCurrentPage(1);
          }} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${vendorFilter === v.id ? 'bg-[#2952C4] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-4439">
                  {v.nombre.split(' ')[0]}
                </button>)}
            </div>}
        </div>

        {/* List */}
        {appLoading && allSales.length === 0 ? <div className="p-16 text-center" data-id="element-4440">
            <div className="w-8 h-8 border-3 border-[#2952C4] border-t-transparent rounded-full animate-spin mx-auto mb-4" data-id="element-4441" />
            <p className="text-slate-500 text-sm" data-id="element-4442">Cargando ventas...</p>
          </div> : allSales.length === 0 ? <div className="p-16 text-center" data-id="element-4443">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-4444">
              <ShoppingCartIcon className="w-8 h-8 text-slate-400" data-id="element-4445" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-4446">No hay ventas</h3>
            <p className="text-slate-500 text-sm" data-id="element-4447">
              No hay ventas registradas en el período seleccionado
            </p>
          </div> : <>
            <div className="divide-y divide-slate-100" data-id="element-4448">
              {paginatedSales.map(sale => {
            const initials = sale.clienteNombre.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
            const avatarColor = getAvatarColor(sale.clienteNombre);
            const hasItbis = sale.itbis > 0;
            return <div key={sale.id} className="px-4 sm:px-6 py-4 hover:bg-slate-50/60 transition-colors" data-id="element-4449">
                    <div className="flex items-start gap-3 sm:gap-4" data-id="element-4450">
                      {/* Avatar */}
                      <div className={`flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center text-sm font-bold ${avatarColor}`} data-id="element-4451">
                        {initials}
                      </div>

                      {/* Main content — vertical */}
                      <div className="flex-1 min-w-0" data-id="element-4452">
                        {/* Row 1: cliente + monto */}
                        <div className="flex items-start justify-between gap-2" data-id="element-4453">
                          <h4 className="font-semibold text-slate-900 text-sm sm:text-base leading-tight" data-id="element-4454">
                            {sale.clienteNombre}
                          </h4>
                          <p className="flex-shrink-0 text-base sm:text-lg font-bold text-[#2952C4]" data-id="element-4455">
                            {formatCurrency(sale.montoTotal)}
                          </p>
                        </div>

                        {/* Row 2: referencia + vendedor + fecha */}
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1" data-id="element-4456">
                          {sale.referencia && <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-0.5" data-id="element-4457">
                              <FileTextIcon className="w-3 h-3" data-id="element-4458" />
                              {sale.referencia}
                            </span>}
                          {(isAdmin || canSeeAll) && sale.vendedorNombre && <span className="inline-flex items-center gap-1 text-xs text-slate-500" data-id="element-4459">
                              <UserIcon className="w-3 h-3" data-id="element-4460" />
                              {sale.vendedorNombre}
                            </span>}
                          <span className="text-xs text-slate-400" data-id="element-4461">
                            {formatDate(sale.fecha)}
                          </span>
                        </div>

                        {/* Row 3: subtotal + itbis breakdown */}
                        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-0.5" data-id="element-4462">
                          <span className="text-xs text-slate-500" data-id="element-4463">
                            Sub Total:{' '}
                            <span className="font-semibold text-slate-700" data-id="element-4464">
                              {formatCurrency(sale.subTotal)}
                            </span>
                          </span>
                          {hasItbis && <span className="text-xs text-slate-500" data-id="element-4465">
                              ITBIS:{' '}
                              <span className="font-semibold text-slate-600" data-id="element-4466">
                                {formatCurrency(sale.itbis)}
                              </span>
                            </span>}
                        </div>

                        {/* Comments */}
                        {sale.comentarios && <p className="mt-1.5 text-xs text-slate-500 italic" data-id="element-4467">
                            {sale.comentarios}
                          </p>}
                      </div>
                    </div>
                  </div>;
          })}
            </div>

            {/* Footer total */}
            <div className="px-4 sm:px-6 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between" data-id="element-4468">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide" data-id="element-4469">
                Total período
              </span>
              <span className="text-base font-bold text-[#2952C4]" data-id="element-4470">
                {formatCurrency(totalVendido)}
              </span>
            </div>

            <div className="p-4 border-t border-slate-100" data-id="element-4471">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={allSales.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-4472" />
            </div>
          </>}
      </Card>

      {/* Ventas por Vendedor — collapsible, full width */}
      {isAdmin && Object.keys(salesByVendor).length > 0 && <Card padding="none" className="animate-stagger-3" data-id="element-4473">
          <button onClick={() => setShowVendorStats(v => !v)} className="w-full px-4 sm:px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-4474">
            <div className="text-left" data-id="element-4475">
              <h2 className="text-base font-bold text-slate-900" data-id="element-4476">Ventas por Vendedor</h2>
              <p className="text-xs text-slate-500 mt-0.5" data-id="element-4477">
                Basado en {invoices.length} facturas
              </p>
            </div>
            {showVendorStats ? <ChevronUpIcon className="w-4 h-4 text-slate-400" data-id="element-4478" /> : <ChevronDownIcon className="w-4 h-4 text-slate-400" data-id="element-4479" />}
          </button>

          {showVendorStats && <div className="px-4 sm:px-6 pb-4 border-t border-slate-100" data-id="element-4480">
              <div className="pt-4 space-y-3" data-id="element-4481">
                {Object.entries(salesByVendor).sort((a, b) => b[1].total - a[1].total).map(([vendedor, data], idx) => {
            const pct = Math.round(data.total / maxVendorTotal * 100);
            const color = getAvatarColor(vendedor);
            return <div key={vendedor} className="flex items-center gap-3 sm:gap-4" data-id="element-4482">
                        {/* Rank */}
                        <span className="flex-shrink-0 w-5 text-xs font-bold text-slate-400 text-right" data-id="element-4483">
                          #{idx + 1}
                        </span>
                        {/* Avatar */}
                        <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${color}`} data-id="element-4484">
                          {vendedor.charAt(0).toUpperCase()}
                        </div>
                        {/* Info + bar */}
                        <div className="flex-1 min-w-0" data-id="element-4485">
                          <div className="flex items-center justify-between mb-1" data-id="element-4486">
                            <span className="text-sm font-semibold text-slate-800 truncate" data-id="element-4487">
                              {vendedor}
                            </span>
                            <div className="flex-shrink-0 flex items-center gap-2 ml-2" data-id="element-4488">
                              <span className="text-xs text-slate-400" data-id="element-4489">
                                {data.count} vtas
                              </span>
                              <span className="text-sm font-bold text-[#2952C4]" data-id="element-4490">
                                {formatCurrency(data.total)}
                              </span>
                            </div>
                          </div>
                          <div className="w-full bg-slate-100 rounded-full h-1.5" data-id="element-4491">
                            <div className="bg-[#2952C4] h-1.5 rounded-full transition-all duration-500" style={{
                    width: `${pct}%`
                  }} data-id="element-4492" />
                          </div>
                        </div>
                      </div>;
          })}
              </div>
            </div>}
        </Card>}
    </div>;
}